import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, X, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '@/context/LangContext';

/**
 * QR code button + modal for sharing invitation links in-person.
 *
 * The coach pulls out their phone, clicks "QR" on an invitation card, shows
 * the screen to a player/parent who scans it with their camera → lands on the
 * register page directly. No typing, no copy-paste.
 */
export default function InviteQRButton({
  url,
  label,
  logoUrl = null,
  variant = 'default', // 'default' | 'compact'
  'data-testid': testId,
}) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const finalLabel = label || t('qrb_default_label');

  const downloadPng = () => {
    const svg = document.querySelector('[data-qr-svg="1"]');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' });
    const u = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas');
      c.width = 512; c.height = 512;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 16, 16, 480, 480);
      c.toBlob(b => {
        if (!b) { toast.error(t('qrb_download_failed')); return; }
        const a = document.createElement('a');
        a.href = URL.createObjectURL(b);
        a.download = `invitacion-${finalLabel.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }, 'image/png');
      URL.revokeObjectURL(u);
    };
    img.src = u;
  };

  const baseBtn = variant === 'compact'
    ? 'inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-gold/40 text-gold hover:bg-gold/10 text-xs font-h uppercase tracking-wider'
    : 'inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10 text-sm font-h uppercase tracking-wider';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={baseBtn}
        data-testid={testId || 'invite-qr-btn'}
        title={t('qrb_show_qr_title')}
      >
        <QrCode className={variant === 'compact' ? 'w-3 h-3' : 'w-4 h-4'} />
        {variant !== 'compact' && <span>QR</span>}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
          data-testid="invite-qr-modal"
        >
          <div
            className="bg-navy-deep border-2 border-gold/60 rounded-lg p-6 max-w-md w-full relative shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 p-1.5 rounded-md text-gold hover:bg-gold/10"
              aria-label={t('qrb_close')}
              data-testid="invite-qr-close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">{t('qrb_scan_to_join')}</div>
            <h2 className="font-h uppercase text-2xl mb-5 text-white">{finalLabel}</h2>

            <div className="bg-white p-5 rounded-md flex items-center justify-center mb-4 relative">
              <QRCodeSVG
                value={url}
                size={256}
                level="H"
                marginSize={1}
                data-qr-svg="1"
                imageSettings={logoUrl ? {
                  src: logoUrl,
                  height: 56,
                  width: 56,
                  excavate: true,
                } : undefined}
              />
            </div>

            <div className="bg-card-steel border border-gold/20 rounded p-2 mb-4 break-all font-mono text-[11px] text-gray-300 max-h-20 overflow-auto">
              {url}
            </div>

            <button
              type="button"
              onClick={downloadPng}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-gold text-navy hover:bg-gold-light font-h uppercase tracking-wider text-sm font-semibold"
              data-testid="invite-qr-download"
            >
              <Download className="w-4 h-4" /> {t('qrb_download_png')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
